<template>
    <div>
        <DCard
            v-if="contractPhases.length === 0"
            padding
            class="notice"
        >
            <DIcon
                name="alert-outline"
                size="32"
            />
            <div>
                <header>{{ __('customers.contracts.invalid_no_phases.title') }}</header>
                <p>{{ __('customers.contracts.invalid_no_phases.text') }}</p>
            </div>
        </DCard>

        <DCard>
            <DTable
                :items="items"
                :fields="fields"
            >
                <template #id="{item}">
                    <td>
                        {{ item.id }}
                    </td>
                </template>
                <template #start_date="{item}">
                    <td>
                        {{ setDateFormat(item.start_date) }}
                    </td>
                </template>
                <template #setup_fee="{item}">
                    <td :class="comparePhaseProperties(item.index, ['setup_fee'])">
                        {{ setupFee(item) }}
                    </td>
                </template>
                <template #price="{item}">
                    <td :class="comparePhaseProperties(item.index, ['price', 'discount_type', 'discount_value', 'discount_name'])">
                        {{ price(item) }}
                    </td>
                </template>
                <template #third_party_cost="{item}">
                    <td :class="comparePhaseProperties(item.index, ['third_party_cost'])">
                        {{ thirdPartyCost(item) }}
                    </td>
                </template>
                <template #billing_cycle="{item}">
                    <td :class="comparePhaseProperties(item.index, ['billing_cycle'])">
                        {{ item.billing_cycle !== null ? item.billing_cycle : '' }}
                    </td>
                </template>
                <template #product_name="{item}">
                    <td :class="comparePhaseProperties(item.index, ['product.name'])">
                        {{ item.product_name }}
                        <DTag
                            v-if="item.id === active_contract_phase_id"
                            color="success"
                            :label="__('messages.active')"
                        />
                    </td>
                </template>
                <template #contract_term="{item}">
                    <td :class="comparePhaseProperties(item.index, ['notice_period', 'notice_period_unit', 'contract_term_unit', 'contract_term'])">
                        {{ contractTerm(item) + ', ' + noticePeriod(item) + extraordinary(item) }}
                    </td>
                </template>
                <template #phase_text="{item}">
                    <td>
                        {{ item.phase_text ?? "" }}
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <DDropdown
                            v-if="isEditableOrDeleteable(contract, item) || setupBilledResettable(item)"
                            :id="`actions_dropdown_${item.id}`"
                            :class="'more-actions-button'"
                            :caret="false"
                            icon="dots-horizontal"
                        >
                            <Link
                                v-if="isEditableOrDeleteable(contract, item)"
                                class="dropdown-link"
                                :href="editContractPhase(item.id)"
                            >
                                <DDropdownItem
                                    :id="`edit_${item.id}`"
                                    icon="pencil"
                                    @click="editContractPhase(item.id)"
                                >
                                    {{ __('customers.common.edit') }}
                                </DDropdownItem>
                            </Link>
                            <DDropdownItem
                                v-if="isEditableOrDeleteable(contract, item)"
                                :id="`delete_${item.id}`"
                                icon="trash-can-outline"
                                @click="deleteContractPhase(item.id)"
                            >
                                {{ __('customers.common.delete') }}
                            </DDropdownItem>
                            <DDropdownItem
                                v-if="setupBilledResettable(item)"
                                :id="`set_setup_fee_to_unpaid_${item.id}`"
                                @click="resetSetupBilled(item.id)"
                            >
                                {{ __('customers.contracts.set_setup_fee_to_unpaid') }}
                            </DDropdownItem>
                        </DDropdown>
                    </td>
                </template>
            </DTable>
        </DCard>

        <DFabs>
            <s-s-o-fab
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
            <DFab
                v-if="
                    contract.status !== 'terminated'
                        && contract.one_time !== true
                        && contract.status !== 'cancelled'
                        && userPermissions.includes('contract.edit')"
                icon="plus"
                :label="__('customers.contracts.add_contract_phase')"
                @click="addContractPhase"
            />
        </DFabs>

        <DModal
            v-model:show="showMissingOrderModal"
            :title="__('customers.contracts.order.missing_order_title')"
            :message="__('customers.contracts.order.missing_order_message')"
            is-error
            @close="showMissingOrderModal = false"
        />
        <DModal
            v-model="activityLogComment"
            v-model:show="showContractPhaseDeleteConfirmationModal"
            :title="__('contracts.contractPhases.delete_confirmation.title')"
            :message="__('contracts.contractPhases.delete_confirmation.message')"
            is-confirm
            is-error
            :show-change-reason-input="true"
            :is-awaiting-validation="true"
            @close="showContractPhaseDeleteConfirmationModal = false"
            @confirm="deleteCallBack"
        />
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <DModal
            v-model="activityLogComment"
            v-model:show="showResetSetupBilledModal"
            :title="__('contracts.contractPhases.reset_setup_billed_modal.title')"
            :message="__('contracts.contractPhases.reset_setup_billed_modal.message')"
            is-confirm
            is-error
            :show-change-reason-input="true"
            :is-awaiting-validation="true"
            @close="showResetSetupBilledModal = false"
            @confirm="resetSetupBilledCallBack"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'CustomerContractPhaseIndex',
    components: { CreateCloudPitCustomerModal, SSOFab },
    mixins: [
        priceFormat
    ],
    layout: [App, CustomerBase],
    props: {
        customer: {
            type: Object,
            default: () => { return {} }
        },
        contractPhases: {
            type: Array,
            default: () => { return [] }
        },
        contract: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'id', label: this.__('customers.contracts.id') },
                { key: 'start_date', label: this.__('customers.contracts.start_date') },
                { key: 'product_name', label: this.__('customers.contracts.product_name') },
                { key: 'phase_text', label: this.__('customers.contracts.phase_text') },
                { key: 'price', label: this.__('customers.contracts.price') },
                { key: 'third_party_cost', label: this.__('customers.contracts.third_party_cost') },
                { key: 'billing_cycle', label: this.__('customers.contracts.billing_cycle') },
                { key: 'setup_fee', label: this.__('customers.contracts.setup_fee') },
                { key: 'contract_term', label: this.__('customers.contracts.contract_term') },
                { key: 'actions', label: '', sorter: false, filter: false }
            ],
            deleteCallBack: () => {},
            showMissingOrderModal: false,
            showCreateCloudpitCustomerModal: false,
            showContractPhaseDeleteConfirmationModal: false,
            active_contract_phase_id: this.contract.active_contract_phase.id,
            activityLogComment: '',
            showResetSetupBilledModal: false,
            currentItem: null
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        setupBilledResettablePhases () {
            const phases = []
            let previousPhase = null
            let phase = null

            const billedUntil = this.contract.billed_until_date ? (new Date(this.contract.billed_until_date)) : null
            let nextInvoice = null
            if (billedUntil === null) {
                nextInvoice = new Date(this.contract.start_date)
            } else {
                billedUntil.setDate(billedUntil.getDate() + 1)
                nextInvoice = billedUntil
            }
            let startDate = null

            for (const i in this.contractPhases) {
                phase = this.contractPhases[i]
                startDate = new Date(phase.start_date)
                if (
                    this.contract.billed_until_date === null ||
                    startDate.getTime() > nextInvoice.getTime()
                ) {
                    if (phase.setup_billed) {
                        phases.push(phase)
                    }
                    continue
                }
                previousPhase = phase
            }
            if (previousPhase !== null && previousPhase.setup_billed) {
                phases.push(previousPhase)
            }
            return phases
        },
        items () {
            const items = []

            for (const contractPhase of this.contractPhases) {
                if (contractPhase.id === this.active_contract_phase_id) {
                    Object.assign(contractPhase, { _classes: 'table-success' })
                    items.push(contractPhase)
                } else {
                    items.push(contractPhase)
                }
            }

            return items
        }
    },
    methods: {
        isEditableOrDeleteable (contract, contractPhase) {
            // If you edit the logic here, please edit it also in (*intentionally* duplicated code):
            // resources/js/Pages/Customers/Contracts/Show.vue
            if (this.contract.status === 'terminated' || this.contract.status === 'cancelled' || contractPhase.id === contract.active_contract_phase.id) {
                return false
            }

            return this.userPermissions.includes('contract.edit')
        },
        addContractPhase () {
            this.$inertia.get(this.$route('customers.customers.contracts.phases.create', { customer: this.$props.customer.id, contract: this.$props.contract.id }))
        },
        editContractPhase (id) {
            return this.$route(
                'customers.customers.contracts.phases.edit',
                { customer: this.customer.id, contract: this.contract.id, phase: id }
            )
        },
        deleteContractPhase (id) {
            this.deleteCallBack = () => {
                this.$inertia.delete(
                    this.$route('customers.customers.contracts.phases.destroy', {
                        customer: this.customer.id,
                        contract: this.contract.id,
                        phase: id,
                        activity_log_comment: this.activityLogComment
                    }),
                    {
                        preserveScroll: true,
                        onSuccess: () => {
                            this.showContractPhaseDeleteConfirmationModal = false
                        },
                        onError: () => {
                            this.showContractPhaseDeleteConfirmationModal = true
                        }
                    }
                )
                this.showContractPhaseDeleteConfirmationModal = false
            }
            this.showContractPhaseDeleteConfirmationModal = true
        },
        contractTerm (item) {
            if (item.contract_term === null) {
                return ''
            }

            const unit = item.contract_term_unit === 'M' ? this.__('customers.contracts.months') : this.__('customers.contracts.days')
            return item.contract_term.toString() + ' ' + unit
        },
        noticePeriod (item) {
            if (item.notice_period === null) {
                return ''
            }

            const unit = item.notice_period_unit === 'M' ? this.__('customers.contracts.months') : this.__('customers.contracts.days')
            return item.notice_period.toString() + ' ' + unit + ' notice'
        },
        extraordinary (item) {
            if (item.extraordinary_termination_allowed === false) {
                return ''
            }

            return ' ' + this.__('customers.contracts.plus_extraordinary')
        },
        setupFee (item) {
            if (item.setup_fee === null) {
                return ''
            }

            let setupFee = 'No fee'
            if (item.setup_fee !== 0.00 || item.setup_fee !== 0.000) {
                const status = item.setup_billed ? this.__('customers.contracts.paid') : this.__('customers.contracts.unpaid')
                setupFee = this.priceFormatForDisplay(item.setup_fee, 2, this.customer.market_region_id) + ' ' + status
            }

            return setupFee
        },
        discount (item) {
            if (!item.discount_value) return ''
            const type = item.discount_type
            if (type === 'percentage') {
                return this.percentageFormatForDisplay(parseFloat(item.discount_value))
            } else {
                return this.priceFormatForDisplay(parseFloat(item.discount_value), 2, this.customer.market_region_id)
            }
        },
        price (item) {
            if (!item.price) return ''
            const price = this.priceFormatForDisplay(parseFloat(item.price), 2, this.customer.market_region_id)
            const discount = this.discount(item)

            if (discount !== '') {
                return price + ', ' + discount + ' ' + item.discount_name
            }

            return price
        },
        thirdPartyCost (item) {
            if (!item.third_party_cost) return ''

            return this.priceFormatForDisplay(parseFloat(item.third_party_cost), 2, this.customer.market_region_id)
        },
        resetSetupBilled (item) {
            this.activityLogComment = ''
            this.showResetSetupBilledModal = true
            this.currentItem = item
        },
        resetSetupBilledCallBack () {
            this.$inertia.post(
                this.$route('customers.customers.contracts.phases.reset-setup-billed', {
                    customer: this.customer.id,
                    contract: this.contract.id,
                    phase: this.currentItem,
                    activity_log_comment: this.activityLogComment
                }),
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.currentItem = null
                        this.showResetSetupBilledModal = false
                        this.activityLogComment = ''
                    },
                    onError: () => {
                        this.showResetSetupBilledModal = true
                    }
                }
            )
            this.showResetSetupBilledModal = false
        },
        setupBilledResettable (item) {
            return this.setupBilledResettablePhases.find((phase) => phase.id === item.id) !== undefined
        },
        comparePhaseProperties (index, properties) {
            if (index === 0) {
                return ''
            }

            let textClass = ''

            properties.forEach((property) => {
                const currentPhase = this.contractPhases[index]
                const previousPhase = this.contractPhases[index - 1]

                const currentValue = this.getPhasePropertyValue(currentPhase, property)
                const previousValue = this.getPhasePropertyValue(previousPhase, property)

                if (currentValue !== previousValue) {
                    textClass = 'text-warning'
                }
            })

            return textClass
        },
        getPhasePropertyValue (object, property) {
            return property.split('.').reduce((o, p) => (o ? o[p] : undefined), object)
        }
    }

}
</script>

<style lang="scss" scoped>
.notice {
    margin-top: 25px;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 15px;

    & .mdi {
        color: #BE423C;
        align-self: center;
    }
    & header {
        font-weight: bold;
        font-size: 1.5em;
    }
    & p {
        margin: 7px 0 0;
        line-height: 1.5em;
        opacity: 0.6;
    }
}
.dropdown-link {
    color: inherit;

    &:hover {
        text-decoration: none;
    }
}
</style>
